import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';

const FamilyWorshipBulkUpload = ({ onClose }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  // 파일 선택 핸들러
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  // 날짜 변환 헬퍼 함수
  const toDbDate = (value) => {
    if (!value) return null;
    if (!value.includes('-')) return value;
    return value.replace(/-/g, '');
  };

  // 빈 문자열을 null로 변환하는 헬퍼 함수
  const toNullIfEmpty = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    return typeof value === 'string' ? value.trim() : value;
  };

  const buildDevotionPayload = (devotion) => ({
    country_cd: devotion.country_cd || 'KR',
    language:
      (devotion.language && String(devotion.language).trim()) ||
      (devotion.country_cd === 'US' ? 'en' : 'ko'),
    devotions_date: toDbDate(devotion.devotions_date),
    title: devotion.title.trim(),
    hymn: toNullIfEmpty(devotion.hymn),
    grace_sharing_text: toNullIfEmpty(devotion.grace_sharing_text),
    scripture_reference: devotion.scripture_reference.trim(),
    scripture_text: devotion.scripture_text.trim(),
    commentary_text: toNullIfEmpty(devotion.commentary_text),
    lets_get_it_text: toNullIfEmpty(devotion.lets_get_it_text),
    etc1: toNullIfEmpty(devotion.etc1),
    closing_text: toNullIfEmpty(devotion.closing_text),
    is_active: devotion.is_active === 'N' ? 'N' : 'Y',
  });

  // 파일 업로드 및 파싱
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      alert('파일을 선택하세요.');
      return;
    }
    setUploading(true);
    setResult(null);

    try {
      const text = await file.text();
      let jsonData;
      try {
        jsonData = JSON.parse(text);
      } catch (err) {
        alert('JSON 파싱 오류: 파일이 올바른 JSON 형식인지 확인하세요.');
        setUploading(false);
        return;
      }

      if (!Array.isArray(jsonData)) {
        alert('JSON 파일은 배열 형식이어야 합니다.');
        setUploading(false);
        return;
      }

      let totalDevotions = 0;
      let totalQuestions = 0;
      const errors = [];

      // 각 가정예배 항목을 순차적으로 처리
      for (const devotion of jsonData) {
        try {
          // 필수 필드 검증
          if (!devotion.devotions_date || !devotion.title || !devotion.scripture_reference || !devotion.scripture_text) {
            const errorMsg = `[${devotion.devotions_date || '알 수 없음'}] 필수 항목(진행일, 제목, 말씀 본문, 본문 내용)이 누락되었습니다.`;
            errors.push(errorMsg);
            continue;
          }

          // 1. family_devotions 테이블에 삽입
          const devotionPayload = buildDevotionPayload(devotion);

          const { data: devotionData, error: devotionError } = await supabase
            .from('family_devotions')
            .insert(devotionPayload)
            .select(
              'id,country_cd,language,devotions_date,title,hymn,grace_sharing_text,scripture_reference,scripture_text,commentary_text,lets_get_it_text,etc1,closing_text,is_active,created_at,updated_at'
            )
            .single();

          if (devotionError) {
            const errorMsg = `[${devotion.devotions_date || devotion.title || '알 수 없음'}] 가정예배 삽입 오류: ${devotionError.message}`;
            console.error(errorMsg);
            errors.push(errorMsg);
            continue;
          }

          const devotionId = devotionData.id;
          totalDevotions++;

          // 2. family_devotions_q 테이블에 삽입
          if (devotion.questions && Array.isArray(devotion.questions) && devotion.questions.length > 0) {
            const questionsToInsert = devotion.questions
              .filter(q => q.question && q.question.trim() !== '')
              .map((question, index) => ({
                devotions_id: devotionId,
                country_cd: devotionPayload.country_cd,
                seq: Number(question.seq) || index + 1,
                question: question.question.trim(),
                reference: toNullIfEmpty(question.reference),
                closing_text: toNullIfEmpty(question.closing_text),
                answer_text: toNullIfEmpty(question.answer_text),
              }));

            if (questionsToInsert.length > 0) {
              const { error: questionsError } = await supabase
                .from('family_devotions_q')
                .insert(questionsToInsert);

              if (questionsError) {
                const errorMsg = `[${devotion.devotions_date || devotion.title || '알 수 없음'}] 질문 삽입 오류: ${questionsError.message}`;
                console.error(errorMsg);
                errors.push(errorMsg);
              } else {
                totalQuestions += questionsToInsert.length;
              }
            }
          }
        } catch (err) {
          const errorMsg = `[${devotion?.devotions_date || devotion?.title || '알 수 없음'}] 처리 오류: ${err.message}`;
          console.error(errorMsg);
          errors.push(errorMsg);
        }
      }

      // 결과 메시지 생성
      let message = '';
      if (errors.length > 0) {
        message = `업로드 완료: 가정예배 ${totalDevotions}건, 질문 ${totalQuestions}건\n\n오류 발생: ${errors.length}건\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n... 외 ${errors.length - 5}건` : ''}`;
      } else {
        message = `성공적으로 업로드되었습니다. 가정예배: ${totalDevotions}건, 질문: ${totalQuestions}건`;
      }

      setResult({ 
        success: errors.length === 0, 
        message: message
      });
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">가정예배 일괄 입력</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>JSON 텍스트 파일 선택</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".json,.txt,application/json,text/plain"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </div>
              <div className="d-flex justify-content-end">
                <button type="button" className="btn btn-secondary mr-2" onClick={onClose} disabled={uploading}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading || !file}>
                  {uploading ? '업로드 중...' : '업로드'}
                </button>
              </div>
            </form>
            {result && (
              <div className={`mt-3 alert ${result.success ? 'alert-success' : 'alert-danger'}`}>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{result.message}</pre>
              </div>
            )}
            <div className="mt-3">
              <small>
                <b>※ 파일 형식</b><br />
                JSON 배열 형태로 각 항목은 다음 구조를 가져야 합니다:<br />
                {'[{'}"devotions_date":"20260719","title":"제목","scripture_reference":"구절","scripture_text":"본문","commentary_text":"해설","lets_get_it_text":"렛츠겟잇","closing_text":"마무리","etc1":"기타","is_active":"Y","questions":[...]{'}]'}<br />
                questions 배열의 각 항목은 question 필드를 포함해야 합니다.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyWorshipBulkUpload;

